import React, { createContext, useCallback, useEffect, useContext } from 'react';
import queryString from 'querystring';
import { useSelector, useDispatch } from 'react-redux';
import { selectTranslations, selectLanguages, selectSelectedLanguage } from 'redux/root.selectors';
import { ILanguage } from 'redux/internationalization/internationalization';
import { IPublicUserAuthorizationDto } from 'redux/session/session';
import { getLanguages, getTranslations } from 'redux/root.actions';
import { parseUrlId } from 'utils';
import { useCurrentUser } from 'hooks';

interface LocalizerContextType {
  translate: (key: string) => string;
}

const LocalizerContext = createContext<LocalizerContextType | {}>({});

const LocalizerProvider = ({ children }: { children: React.ReactNode }) => {
  const query = queryString.parse(window.location.search.substr(1));
  const { urlId } = query;

  const dispatch = useDispatch();

  const currentUser: IPublicUserAuthorizationDto | null = useCurrentUser();
  const languages: ILanguage[] = useSelector(selectLanguages);
  const selectedLanguage: ILanguage | null = useSelector(selectSelectedLanguage);
  const translations: Record<string, any> = useSelector(selectTranslations);

  // NOTE: Load languages based on current user context as languages are configured by customer
  useEffect(() => {
    if (!currentUser) return;
    if (!languages.length) dispatch(getLanguages());
  }, [dispatch, languages, currentUser]);

  // NOTE: Load translations based on selected language
  useEffect(() => {
    if (!selectedLanguage) return;

    const parsedUrlId = parseUrlId(urlId as string);
    if (parsedUrlId) dispatch(getTranslations(selectedLanguage, parsedUrlId));
  }, [dispatch, selectedLanguage, urlId]);

  // NOTE: We use this to search key value in the flattened translations
  const translate = useCallback((key: string) => {
    if (!translations || Object.keys(translations).length === 0) return key;
    const translation = translations[key];
    if (translation) return translation;
    return key;
  }, [translations]);

  return (
    <LocalizerContext.Provider value={{ translate }}>
      {children}
    </LocalizerContext.Provider>
  );
};

// These types are used to define structure of translate function in order to have better type safety
export type TranslateToStringFunction = (key: string, values?: { [x: string]: string; })
  => string;

export type TranslateFunction = (key: string, values?: { [x: string]: string; })
  => React.ReactNode;

export type TranslateUnsafeFunction = (key: string, values?: { [x: string]: string; })
  => React.ReactNode;

// Wrapper around the context so we do not couple the components to the context (LocalizerContext) :)
const useTranslate = () => {
  const { translate: translateKey } = useContext(LocalizerContext) as LocalizerContextType;

  const translateToString = (key: string, values?: { [x: string]: string; }) : string => {
    let value = translateKey(key);

    if (values) {
      Object.keys(values).forEach((key) => {
        const regex = new RegExp(`\\$\\{${key}\\}`, 'g');
        value = value.replace(regex, values[key]);
      });
    }

    return value;
  };

  const translate = (key: string, values?: { [x: string]: string; }) : React.ReactNode => (
    <span className={key}>{translateToString(key, values)}</span>
  );

  const translateUnsafe = (key: string, values?: { [x: string]: string; }) : React.ReactNode => (
    // eslint-disable-next-line react/no-danger
    <span className={key} dangerouslySetInnerHTML={{ __html: translateToString(key, values) }} />
  );

  return {
    translate,
    translateUnsafe,
    translateToString
  };
};

export interface TranslateProps { id: string; }
/**
 * A functional component that translates a given identifier into a localized string.
 * NB! This is to support legacy code that used rect-localize-redux and is too heavy to refactor out
 * If possible, use the useTranslate hook instead
 *
 * @param translateProps - The properties for the Translate component.
 * @param translateProps.id - The identifier for the string to be translated.
 * @returns The translated string corresponding to the provided identifier.
 */
const Translate = (translateProps: TranslateProps) => {
  const { id } = translateProps;
  const { translate } = useTranslate();
  return (
    <span className={id}>{translate(id)}</span>
  );
};

export {
  LocalizerProvider,
  useTranslate,
  Translate
};
